<script>
   /*************************************************************/
   /* Svelte component for calculation of PV-set scalars        */
   /*************************************************************/

   import { max, min } from 'mdatools/stat';
   import { splitregdata } from 'mdatools/models';
   import { Select, Container, ButtonCancel, Button, Spinner } from './controls';
   import { pcvpls } from './wasm/pcvpls.js';
   import PCV from './wasm/pcv_wasm.js';

   // main properties of the whole app
   export let dataset = null;
   export let model = null;
   export let scalars_ini = null;
   export let pvoptions = null;

   // error handling parameetrs
   let status = 'neutral';
   let errMsg = null;

   // PV-set calculation parameters
   let cvtypeOptions = ['ven', 'rand', 'loo'];
   let cvType = 'ven';
   let promise;

   // compute cross-validation options based on dataset size
   let nsegOptions = dataset && dataset.values.nrows > 0 ?
      [2, 3, 4, 5, 10, 20].filter(v =>
         v >= (dataset.values.nrows / (dataset.values.nrows - model.ncomp - 1)) && v <= dataset.values.nrows / 2
      ) : [];

   let nseg = nsegOptions[0];


   // if dataset is too small (less than 4 rows) - limit CV choice by LOO
   $: if (dataset.values.nrows < 4 || nsegOptions.length === 0) {
      cvtypeOptions = ['loo'];
      cvType = 'loo';
   }

   // function that computes PV-set using given parameters
   async function computePVSet(data, model, cv) {
      promise = PCV()
         .then(Module => {
            const cv_type = cv.type == 'loo' ? 0 : cv.type == 'rand' ? 2 : 1;
            const [X, Y] = splitregdata(data);
            const [a, b] = pcvpls(Module, model, X, Y, model.ncomp, cv.nseg, cv_type, 0);

            const ncomp1v = b.apply(max, 2).which(v => v > 2.0);
            const ncomp2v = b.apply(min, 2).which(v => v < 0.0);
            const ncomp1 = ncomp1v.length === 0 ? model.ncomp + 1 : ncomp1v.v[0];
            const ncomp2 = ncomp2v.length === 0 ? model.ncomp + 1 : ncomp2v.v[0];
            let ncomp = Math.min(ncomp1, ncomp2) - 1;
            if (ncomp < 1) ncomp = 1;
            pvoptions = {type: cv_type, nseg: cv.nseg, ncompRec: ncomp, ncompSel: ncomp };
            scalars_ini = b;
         })
         .catch((e) => {
            scalars_ini = null;
            pvoptions = null;
            status = 'error';
            errMsg = e;
         });
   }

   // event handler which triggers the model computation
   function compute(e) {
      status = 'neutral';
      errMsg = null;
      computePVSet(dataset.values, model, {type: cvType, nseg: nseg});
   }

   // function which resets the state of the component
   function reset() {
      status = 'neutral';
      errMsg = null;
      promise = null;
      scalars_ini = null;
      pvoptions = null;
   }

   // if data file is empty reset status and error messages
   $: if (model === null) reset();

</script>


{#if (model && !scalars_ini) && status !== 'busy'}

   <Container label='Split type:'>
      <Select id='cvType' bind:value={cvType} options={cvtypeOptions} />
   </Container>

   {#if cvType !== 'loo'}
   <Container label='Segments:'>
      <Select id='nseg' bind:value={nseg} options={nsegOptions} />
   </Container>
   {/if}

   <Container label=' '>
      <Button on:click={compute}>Compute PV-set</Button>
   </Container>
{/if}

{#if promise}

   {#await promise}
      <Container>
         <Spinner />
         <p>Computing PV-set...</p>
      </Container>
   {:then value}
      {#if scalars_ini !== null}
      <Container>
         <p>PV-set is pre-computed: {cvType} {cvType !== 'loo' ? nseg + ' seg' : ''}</p>
         <ButtonCancel on:click={reset} />
      </Container>
      {/if}

   {:catch error}
      <Container status='error'>
         <p>{error}</p>
      </Container>
   {/await}

{/if}

{#if status === 'error' && errMsg}
   <Container status='error'>
      <p>{errMsg}</p>
   </Container>
{/if}

